import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import env from '@polleverywhere/core/lib/env'
import subscribe from '@polleverywhere/firehose_client'

const BLANK = '--:--'
const ZERO = '0:00'
const INVALID_TIME_CHARACTERS = /[^\d]+/g
const MS_PER_SECOND = 1000
const PUSH_PARAMS = [
  'auto',
  'voting_instructions'
]

function shouldStartTimer () {
  const startFromParam = PUSH_PARAMS.includes(this.settings.get('push'))
  const isCorrect = this.settings.get('display').includes('correctness')

  return (
    (this.isFullscreen || this.settings.autoStartTimer() || startFromParam) &&
    this.listenToFirehose &&
    !isCorrect &&
    this.activity.isActivated()
  )
}

async function handleTimerBehavior (isVisible) {
  try {
    if (this.isReadOnly) { return }
    if (isVisible && shouldStartTimer.call(this)) {
      if (this.isRunning()) {
        await this.stop()
      }

      await this.reset()
      await this.start()
    } else {
      await this.stop()
      await this.reset()
    }
  } catch (e) { }
}

const requestAnimationFrame = (() =>
  window.requestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window.mozRequestAnimationFrame ||
  window.oRequestAnimationFrame ||
  window.msRequestAnimationFrame ||
  ((callback, element) => window.setTimeout(callback, 1000 / 60)))()

const cancelAnimationFrame = (() =>
  window.cancelAnimationFrame ||
  window.webkitCancelRequestAnimationFrame ||
  window.mozCancelRequestAnimationFrame ||
  window.oCancelRequestAnimationFrame ||
  window.msCancelRequestAnimationFrame ||
  clearTimeout)()

const millisecondsToFormattedTime = function (ms) {
  const totalSeconds = ms / MS_PER_SECOND
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = padWithZero(Math.floor(totalSeconds % 60))

  return `${minutes}:${seconds}`
}

// replace invalid characters, only keep the last 4
const normalizeTime = time =>
  time.replace(INVALID_TIME_CHARACTERS, '').slice(-4)

const processMinutesAndSeconds = function (normalizedTime, addSeconds) {
  const digits = normalizedTime.split('')
  // Use + for more expected behavior
  // parseInt("") => NaN
  // +("") => 0
  let seconds = +digits.splice(-2, 2).join('')
  let minutes = +digits.splice(0, 2).join('')

  if (addSeconds) {
    if (seconds > 60 && seconds < 100) {
      minutes += Math.floor(seconds / 60)
      seconds = seconds % 60
    }
  }

  seconds = padWithZero(seconds)

  return [minutes, seconds]
}

var padWithZero = str => `00${str}`.slice(-2)

const formattedTime = function (time, addSeconds) {
  const normalized = normalizeTime(time)
  return processMinutesAndSeconds(normalized, addSeconds).join(':')
}

export default class Timer extends Backbone.Model {
  initialize (opts) {
    this.activity = opts.activity
    this.settings = opts.settings
    this.isFullscreen = opts.isFullscreen
    this.isReadOnly = opts.isReadOnly
    this.listenToFirehose = opts.listenToFirehose
    this.shouldStartTimer = shouldStartTimer.call(this)
    this.timerId = null
    this.lastTimestamp = null
    this.millisecondsRunning = 0
    this.debouncedSave = _.debounce(this.save.bind(this), 500)

    if (this.listenToFirehose) {
      this.listenTo(this.settings, 'change:visible', (model, value) => {
        handleTimerBehavior.call(this, value)
      })

      handleTimerBehavior.call(this, true)
    }

    const countdownTime = this.activity.get('countdown_seconds') || 0
    const formattedTime = millisecondsToFormattedTime(countdownTime * 1000)
    this.set({ countdownSeconds: formattedTime })

    if (this.listenToFirehose) {
      this.connectFirehose()
    }
  }

  connectFirehose () {
    this.firehoseChannel = subscribe(`/${this.activity.get('presenter_key')}/timer`, {
      host: env.get('firehose_url').toString(),
      ssl: env.get('firehose_ssl')
    })

    this.firehoseChannel.on('message', this.firehoseProcessData.bind(this))

    if (this.shouldStartTimer) {
      this.firehoseChannel.once('connected', this.start)
    }

    return this.firehoseChannel
  }

  syncTime (val) {
    return this.set('countdownSeconds', val)
  }

  destroy () {
    this.firehoseChannel && this.firehoseChannel.unsubscribe()
    return super.destroy()
  }

  save () {
    return this.activity.save({
      countdown_seconds: this.toMilliseconds() / 1000 || 0
    }, { patch: true }).done(() => {
      this.publishToFirehose('set', 'PUT',
        { time: this.toString() })
    })
  }

  toggleTimer () {
    if (this.isReadOnly) { return }
    this.isRunning() ? this.stop() : this.start()
  }

  isTimeSet () {
    return ![ZERO, BLANK, ''].includes(this.toString())
  }

  publishToFirehose (path, httpVerb = 'POST', data = {}) {
    const url = env
      .get('api_url')
      .segment('api/timer')
      .segment(path)
      .toString()

    data.user_id = this.activity.get('owner_id')
    data.presenter_key = this.activity.get('presenter_key')

    const options = {
      url,
      data,
      method: httpVerb
    }

    return Backbone.ajax(options)
  }

  isRestarting () {
    return this.millisecondsRunning > this.toMilliseconds()
  }

  // Make sure we can only set in the format mm:ss
  setTime (newTime, addSeconds = true) {
    const formatted = formattedTime(newTime, addSeconds)
    if (formatted === ZERO) { return }

    this.syncTime(formatted)

    return this.debouncedSave()
  }

  start () {
    return new Promise((resolve, reject) => {
      if (this.timerId) return resolve()
      if (!this.isTimeSet()) return resolve()

      this.stopped = false
      this.lastTimestamp = Date.now()

      if (this.isRestarting()) {
        this.millisecondsRunning = 0
      }

      Promise.all([
        this.publishToFirehose('start', 'POST', {
          timestamp: this.lastTimestamp,
          milliseconds_running: this.millisecondsRunning
        })
      ]).then(resolve, reject)
    })
  }

  stop () {
    this.stopped = true

    return new Promise((resolve, reject) => {
      if (!this.timerId) return resolve()
      if (!this.isTimeSet()) return resolve()

      Promise.all([this.publishToFirehose('stop')]).then(resolve, reject)
    })
  }

  reset () {
    return this.publishToFirehose('reset')
  }

  clear () {
    return this.publishToFirehose('clear', 'DELETE')
  }

  update () {
    const now = Date.now()
    const dt = now - this.lastTimestamp
    this.lastTimestamp = now

    this.millisecondsRunning += dt
    const remaining = this.toMilliseconds() - this.millisecondsRunning

    this.checkWarning(remaining)

    if (remaining <= 0) {
      this.stop()
      this.set({
        runningStatus: 'stopped',
        percentFinished: 100,
        countdownSeconds: '0:00',
        showWarning: true
      })

      // We cancel the animation frame callback whenever a poll is stopped, but
      // sometimes the callback that should be stopped has already fired, which
      // causes the timer to not stop.
    } else if (!this.stopped) {
      this.set({
        runningStatus: 'running',
        percentFinished: (this.millisecondsRunning / this.toMilliseconds()) * 100,
        countdownSeconds: millisecondsToFormattedTime(remaining),
        showWarning: remaining <= (10 * MS_PER_SECOND)
      })
      this.timerId = requestAnimationFrame(this.update)
    }
  }

  isRunning () {
    return !!this.timerId
  }

  toMilliseconds () {
    const [minutes, seconds] = Array.from(this.toString().split(':'))

    let totalSeconds = Number(seconds)
    totalSeconds += Number(minutes) * 60

    return totalSeconds * MS_PER_SECOND
  }

  toString () {
    return this.get('countdownSeconds')
  }

  firehoseProcessData (msg) {
    switch (msg.action) {
      case 'set':
        this.millisecondsRunning = 0
        this.trigger('set', msg.data.time)
        this.update()
        break
      case 'start':
        this.lastTimestamp = +msg.data.timestamp
        this.millisecondsRunning = +msg.data.milliseconds_running
        this.trigger('start')
        this.update()
        break
      case 'stop':
        cancelAnimationFrame(this.timerId)
        this.timerId = null
        this.trigger('stop')
        this.update()
        break
      case 'reset':
        cancelAnimationFrame(this.timerId)
        this.timerId = null
        this.millisecondsRunning = 0
        this.trigger('reset')
        this.update()
        break
      case 'clear':
        this.syncTime(BLANK)
        this.save(BLANK)
        this.trigger('clear')
        break
    }
  }
}
